import {
  exam_getExaminationInfoList_Api,
  exam_getExaminationReportGroupByInfoSummary_Api,
  examinationSchema,
} from 'apis'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo} from 'react'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {useSelectEntities} from 'redux/entities/useSelectEntities'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault, renderElse} from 'views/Shared'
import {renderIf} from '../../Shared'
import {ExaminationReportGroupByInfosFilter} from './ExaminationReportGroupByInfosFilter'
import {withReportTable} from './renderReportTable'
import {useRouteSearchParam} from "../../../envs/_current/history";
import {formatDate} from "../../../helpers/datetimeHelpers";
import dayjs from "dayjs";

const headers = [
  'total exam paper',
  'count 100',
  'average exam time',
  'max point',
]

const schemas = [
  {
    name: 'total exam paper',
    value_path: 'total_count',
    className: 'font-semibold text-primary w-10',
  },
  {
    name: 'count 100',
    value_path: 'total_count_highest',
    className: 'font-semibold text-primary w-10',
  },
  {
    name: 'average exam time',
    value_path: 'avg_exam_time',
    value2_path: 'min_exam_time',
    className: 'text-color-100 w-16',
    render: (value, data) => {
      if (isNaN(value)) return value
      return (
        <span>{dayjs.unix(value).utc().format('HH:mm:ss')}</span>
      )
    },
    render2: (value, data) => {
      return (
        <Translate>
          {(t) => (
            <div className="text-2xs italic whitespace-no-wrap">
              {`${t('min time')} : `}
              <span>
                {dayjs.unix(value).utc().format('HH:mm:ss')}
              </span>
            </div>
          )}
        </Translate>
      )
    },
  },
  {
    name: 'max point',
    value_path: 'max_point',
    value2_path: 'target_point',
    className: 'font-semibold text-primary w-10',
    render2: (value, data) => {
      return <span className="font-light">{` / ${value}`}</span>
    },
  },
]

const ExaminationInfo = ({id}) => {
  const t = useTranslate()

  const item = useSelectEntities(id, examinationSchema, {})

  const {time_open, time_close, total_examination_paper} = item || {}

  const ItemInformation = ({label, value}) => (
    <div className="flex flex-col items-start">
      <span className="font-light text-xs text-color-400 italic">
        {t(label)}
        {': '}
      </span>
      <span className="font-semibold text-xs text-color-000 leading-tight">
        {renderIf(value, value)}
        {renderElse(value, '--')}
      </span>
    </div>
  )

  return renderElse(
    _.isEmpty(item),
    <div className="flex flex-col gap-4 mb-10 mt-4">
      <div className="text-center text-xl font-bold text-primary">
        {getTitle(item)}
      </div>
      <div className="grid grid-cols-3 gap-2 px-8">
        <ItemInformation
          label="start"
          value={formatDate(time_open,'HH:mm - DD/MM/YYYY')}
        />
        <ItemInformation
          label="end"
          value={formatDate(time_close,'HH:mm - DD/MM/YYYY')}
        />
        <ItemInformation
          label="total exam paper"
          value={total_examination_paper}
        />
      </div>
    </div>
  )
}

const ExaminationReportGroupByInfos = ({
                                         owner_id,
                                         owner_type,
                                         Wrapper = 'div',
                                         filter_actions = {
                                           sortBy: 'sort-by',
                                           keyword: 'keyword',
                                           examination_id: 'examination',
                                           filter_examination_info_id: 'display',
                                           examination_info_answer_id: 'answer',
                                           examination_info_id: 'info',
                                         },
                                       }) => {
  const location = useLocation()

  const t = useTranslate()

  const sortBy = useRouteSearchParam(filter_actions.sortBy)

  const examination_id = useRouteSearchParam(filter_actions.examination_id)

  const filter_examination_info_id = useRouteSearchParam(
    filter_actions.filter_examination_info_id
  )

  const examination_info_id = useRouteSearchParam(filter_actions.examination_info_id)

  const examination_info_answer_id = useRouteSearchParam(
    filter_actions.examination_info_answer_id
  )

  const apiInfo = exam_getExaminationInfoList_Api

  const {response, isLoading} = useAsyncWithCache({
    cacheId: [
      apiInfo.path.replace(':id', examination_id),
      JSON.stringify({}),
    ].join('/'),
    apiInfo,
    values: {},
    // deps: [{}],
    query: bindQueryParam({id: examination_id}),
  })

  const refresh_deps = useMemo(
    () => [
      owner_id,
      owner_type,
      `refreshToken=${_.get(location.state, 'refreshToken')}`,
    ],
    [owner_id, owner_type, location.state]
  )
  const selectedFields = _.isEmpty(examination_info_id)
    ? _.get(response, 'data.data', [])
      .filter((e) => !!e.is_mandatory)
      .slice(0, 1)
    : _.get(response, 'data.data', []).filter((e) =>
      [examination_info_id].includes(e.id)
    )

  const customHeaders = [...selectedFields.map((e) => e.label), ...headers]
  const customSchemas = [
    ...selectedFields.map((e) => {
      return {
        name: e.id,
        value_path: e.id,
        className: 'flex text-left font-medium text-color-000',
      }
    }),
    ...schemas,
  ]

  const [pager_widget, {widget_header}] = usePaginationHelper({
    onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
      onSortChange(sortBy)
      onFilterChange({
        examination_id,
        examination_info_id,
        filter_examination_info_id,
        examination_info_answer_id,
      })
    },
    pagerInfo: {
      query: bindQueryParam({
        id: examination_id,
      }),
      PaginationMeta: NullAsDefault,
      apiInfo: exam_getExaminationReportGroupByInfoSummary_Api,
      ...withReportTable(customHeaders, customSchemas),
    },
    withKey: createPagerKey([...refresh_deps, selectedFields]),
    dependencies: [
      owner_id,
      owner_type,
      examination_id,
      examination_info_id,
      filter_examination_info_id,
      examination_info_answer_id,
    ],
    filter_params: {
      owner_id,
      owner_type,
      examination_id,
      examination_info_id,
      filter_examination_info_id,
      examination_info_answer_id,
    },
    SortByWidget: NullAsDefault,
    renderSearchBar: NullAsDefault,
    isToggleDefault: true,
    query_params: filter_actions,
    AdvancedFilter: ExaminationReportGroupByInfosFilter,
  })
  return (
    <Wrapper className="space-y-3">
      <div className="font-semibold text-primary tracking-wide uppercase">
        {t('report')}
      </div>
      <div className="border rounded-lg p-3">
        <ExaminationInfo id={examination_id}/>
        <div className="flex flex-col">
          <div className="text-center font-bold text-lg text-color-000 uppercase tracking-widest mb-4">
            {t('exam report group by info')}
          </div>
          {examination_id ? pager_widget : widget_header}
        </div>
      </div>
    </Wrapper>
  )
}

export default compose(
  branch(
    ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
    () => () => <ContentNotFound/>
  )
)(ExaminationReportGroupByInfos)
